/**
 * @module app.GeolocationService
 */
import appBase from './index.js';
import olFeature from 'ol/Feature.js';
import olGeolocation from 'ol/Geolocation.js';
import olGeomPoint from 'ol/geom/Point.js';
import olLayerVector from 'ol/layer/Vector.js';
import olSourceVector from 'ol/source/Vector.js';
import olStyleCircle from 'ol/style/Circle.js';
import olStyleFill from 'ol/style/Fill.js';
import olStyleStroke from 'ol/style/Stroke.js';
import olStyleStyle from 'ol/style/Style.js';
import {Z_INDEX} from './constants.js';
import {equals} from 'ol/coordinate';

/**
 * @constructor
 */
class GeolocationService {
  constructor() {
    /**
     * @type {ol.Map}
     * @private
     */
    this.map_ = null;

    /**
     * @type {ol.View}
     * @private
     */
    this.view_ = null;

    /**
     * @type {ol.Geolocation}
     */
    this.geolocation = null;

    /**
     * @type {boolean}
     * @private
     */
    this.trackOnce_ = false;

    /**
     * @type {ol.geom.Point}
     * @private
     */
    this.positionPoint_ = new olGeomPoint([0, 0]);

    /**
     * @type {ol.Feature}
     * @private
     */
    this.positionFeature_ = new olFeature(this.positionPoint_);

    /**
     * @type {ol.Feature}
     * @private
     */
    this.accuracyFeature_ = new olFeature();

    /**
     * @type {ol.source.Vector}
     * @private
     */
    this.source_ = new olSourceVector();
  }

  /**
   * @param {!ol.Map} map
   */
  init(map) {
    this.map_ = map;

    this.view_ = map.getView();

    this.geolocation = new olGeolocation({
      projection: this.view_.getProjection(),
      trackingOptions: /** @type {GeolocationPositionOptions} */ ({
        maximumAge: 10000,
        enableHighAccuracy: true,
        timeout: 600000
      })
    });

    this.geolocation.on('change:accuracyGeometry', () => {
      this.accuracyFeature_.setGeometry(this.geolocation.getAccuracyGeometry());
    });

    const vectorLayer = new olLayerVector({
      'altitudeMode': 'clampToGround',
      'name': 'geolocation',
      zIndex: Z_INDEX.UP,
      source: this.source_,
      style: new olStyleStyle({
        image: new olStyleCircle({
          fill: new olStyleFill({
            color: 'rgba(255,255,255,0.4)'
          }),
          stroke: new olStyleStroke({
            color: '#F00',
            width: 1.25
          }),
          radius: 5
        }),
        fill: new olStyleFill({
          color: 'rgba(255,255,255,0.4)'
        }),
        stroke: new olStyleStroke({
          color: '#F00',
          width: 1.25
        })
      })
    });

    // FIXME: use vectorLayer.setMap(map)
    this.map_.addLayer(vectorLayer);

    this.geolocation.on('change:position', (event) => {
      if (this.geolocation !== null && this.view_ !== null) {
        const coordinates = this.geolocation.getPosition();
        console.assert(coordinates);
        const center = this.view_.getCenter();
        console.assert(center);
        if (!equals(coordinates, center)) {
          this.updatePosition_(coordinates);
        }
        if (this.trackOnce_ === true) {
          this.deactivateTracking_();
        }
      }
    });
  }

  trackOnce() {
    if (this.geolocation) {
      if (this.geolocation.get('tracking') === false) {
        this.trackOnce_ = true;
        this.activateTracking_();
      }
    }
  }

  /**
   * @private
   */
  activateTracking_() {
    if (this.geolocation) {
      this.addFeatures_();
      this.geolocation.set('tracking', true);
      gtag('event', 'geolocation');
    }
  }

  /**
   * @private
   */
  deactivateTracking_() {
    if (this.geolocation !== null && this.view_ !== null) {
      this.geolocation.set('tracking', false);
      if (this.trackOnce_ === true) {
        this.trackOnce_ = false;
        this.view_.once('change:center', () => {
          this.source_.clear();
        });
      }
    }
  }

  /**
   * @private
   */
  addFeatures_() {
    if (this.source_.getFeatures().length === 0) {
      this.source_.addFeatures([this.accuracyFeature_, this.positionFeature_]);
    }
  }

  /**
   * @param {ol.Coordinate} coordinates
   * @private
   */
  updatePosition_(coordinates) {
    this.positionPoint_.setCoordinates(coordinates);
    this.view_.setCenter(coordinates);
    this.view_.setZoom(8);
  }
}

appBase.service('appGeolocationService', GeolocationService);


export default GeolocationService;
